import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router';
import { withAuth } from '@jg/auth-react';
import { useTracking } from '@jg/react-tracking';
import PageCard from '@jg/page-card';
import {
  getCharity,
  getTeamName,
  getHeroImage,
  getEventData,
  getShortName,
} from '../../selectors/apiSelectors';
import { IAppState } from '../../types/state.d';
import InviteMembersModal from '../../components/InviteMembersModal';
import { apiThunk } from '../../actions';
import config from '../../config';
import { makePhotoUrl, makePhotoSquare } from '../../utils';
import {
  IYourTeamPage,
  IYourTeamPageMapStateToProps,
  IYourTeamPageMapDispatchToProps,
} from './YourTeamPage.d';
import { getLocks } from '../../selectors/locksSelectors';

export const YourTeamPage: FunctionComponent<IYourTeamPage> = ({
  charityData,
  heroImage,
  teamName,
  auth,
  eventData,
  shortName,
  fetchEventDetails,
}) => {
  const track = useTracking();
  useEffect(() => {
    track({
      subtype: 'page creation',
      event_value: 'youve got yourself a team',
      page_section: 'page creation > youve got yourself a team',
    }); // Why are we tracking like this?
  }, []);
  useEffect(() => {
    fetchEventDetails(); // Do we need this?
  }, []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const accessToken = auth.getAccessToken();
  const memberPhoto =
    accessToken && makePhotoUrl(makePhotoSquare(accessToken.profilePictureUrl));
  const membersList = [{ picture: memberPhoto, id: '1' }];
  const coverPhoto = makePhotoUrl(heroImage || config.defaultCoverPhoto);

  return (
    <main>
      <Helmet title={t('title.createTeam.success')} />
      <h1 className="jg-h1 jg-space-mvml jg-space-mtxs container--heading">
        {t(['content.yourteampage.heading', 'You’ve got yourself a team!'])}
      </h1>
      <h2 className="jg-h2 jg-space-mbxs">
        {t(['content.yourteampage.yourteampage', 'Your team page'])}
      </h2>
      <p className="jg-text--large jg-space-mbsm">
        {t([
          'content.yourteampage.yourteampage.subtext',
          'Invite friends to join your team page and start fundraising together',
        ])}
      </p>
      <PageCard
        coverPhoto={coverPhoto}
        pageName={teamName}
        charityName={charityData.name}
        usersList={membersList}
        usersNumber={1}
        eventName={eventData!.name}
        fundraisingForLabel={t([
          'content.yourteampage.fundraisingfor',
          'Fundraising for',
        ])}
        userLabel={t(['content.yourteampage.member', 'member'])}
        usersLabel={t(['content.yourteampage.members', 'members'])}
        eventLabel={t(['content.yourteampage.event', 'Event'])}
        link={`${config.basePageUri}${shortName}`}
        onClick={() => {
          track({
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'your team page',
            page_section: 'page creation > youve got yourself a team',
          });
        }}
      />
      <div className="jg-tc-button-container">
        <button
          data-qa="invite-team-members"
          className="jg-btn jg-space-mtmd"
          onClick={() => {
            setIsModalOpen(true);
            track({
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'invite team members',
              page_section: 'page creation > youve got yourself a team',
            });
          }}
          aria-haspopup="true"
          type="button"
        >
          {t([
            'content.yourteampage.button.inviteteammembers',
            'Invite team members',
          ])}
        </button>
        <a
          href={`${config.basePageUri}${shortName}`}
          className="jg-btn jg-space-mbms jg-space-mtmd"
        >
          {t('global.button.gotomypage')}
        </a>
      </div>
      <InviteMembersModal
        shareMessage={t('global.shareMessage')}
        shareHeader={t('global.shareHeader')}
        shareURL={`${config.basePageUri}${shortName}?invite=true`}
        onCloseModal={() => setIsModalOpen(false)}
        fbAppId={config.fbAppId}
        isModalOpen={isModalOpen}
      />
    </main>
  );
};

export const mapDispatchToProps: IYourTeamPageMapDispatchToProps = {
  fetchEventDetails: apiThunk.fetchEventDetails,
};

export const mapStateToProps = (
  state: IAppState,
): IYourTeamPageMapStateToProps => ({
  teamName: getTeamName(state),
  heroImage: getHeroImage(state),
  charityData: getCharity(state),
  eventData: getEventData(state),
  shortName: getShortName(state),
  teamlocks: getLocks(state),
});

export default withAuth(
  withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(YourTeamPage),
  ),
);
